import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  Heading,
  Divider,
  Progress,
  Tooltip,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiTrash2, FiZap } from 'react-icons/fi';
import { useDebugStore } from '../../../../stores/debugStore';
import { useWebSocketDebug } from '../hooks/useWebSocketDebug';
import styles from '../PersonalizationDebugPanel.module.css';

const ConceptDetectionLog: React.FC = () => {
  const { detectedConcepts, clearDetectedConcepts } = useDebugStore();
  const { simulateConceptDetection } = useWebSocketDebug();
  
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const getPerformanceColor = (performance: string) => {
    switch (performance) {
      case 'mastered':
        return 'blue';
      case 'understood':
        return 'green';
      case 'struggled':
        return 'red';
      case 'confused':
        return 'orange';
      case 'mentioned':
      default:
        return 'gray';
    }
  };

  const getPerformanceIcon = (performance: string) => {
    switch (performance) {
      case 'mastered':
        return '🎯';
      case 'understood':
        return '✅';
      case 'struggled':
        return '⚠️';
      case 'confused':
        return '❓';
      case 'mentioned':
      default:
        return '💬';
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between">
        <Heading size="md">Concept Detection Log</Heading>
        <HStack>
          <Button
            size="sm"
            leftIcon={<FiZap />}
            variant="outline"
            onClick={simulateConceptDetection}
          >
            Simulate
          </Button>
          <Button
            size="sm"
            leftIcon={<FiTrash2 />}
            variant="ghost"
            onClick={clearDetectedConcepts}
            isDisabled={detectedConcepts.length === 0}
          >
            Clear
          </Button>
        </HStack>
      </HStack>

      <Divider />

      {detectedConcepts.length === 0 ? (
        <Box
          p={8}
          textAlign="center"
          borderWidth={1}
          borderColor={borderColor}
          borderRadius="md"
          borderStyle="dashed"
        >
          <Text color="gray.500">No concepts detected yet</Text>
          <Text fontSize="sm" color="gray.400" mt={2}>
            Concepts will appear here as you chat
          </Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing={3}>
          {detectedConcepts.map((concept, index) => (
            <Box
              key={`${concept.skill_code}-${concept.timestamp}-${index}`}
              p={3}
              bg={bgColor}
              borderRadius="md"
              borderWidth={1}
              borderColor={borderColor}
              className={index === 0 ? styles.newItem : ''}
            >
              <HStack justify="space-between" mb={2}>
                <HStack>
                  <Text fontSize="sm" fontWeight="medium">
                    {getPerformanceIcon(concept.performance)}
                  </Text>
                  <Text fontWeight="medium" fontSize="sm">
                    {concept.skill_name || concept.skill_code}
                  </Text>
                </HStack>
                <Badge
                  colorScheme={getPerformanceColor(concept.performance)}
                  variant="subtle"
                >
                  {concept.performance}
                </Badge>
              </HStack>

              <HStack spacing={4} mb={2}>
                <Text fontSize="xs" color="gray.500">
                  Code: {concept.skill_code}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {new Date(concept.timestamp).toLocaleTimeString()}
                </Text>
              </HStack>

              <Tooltip label={`Confidence: ${Math.round(concept.confidence * 100)}%`}>
                <Progress
                  value={concept.confidence * 100}
                  size="xs"
                  colorScheme={concept.confidence > 0.7 ? 'green' : concept.confidence > 0.4 ? 'yellow' : 'red'}
                  borderRadius="full"
                />
              </Tooltip>

              {concept.notes && (
                <Text fontSize="xs" color="gray.600" mt={2} fontStyle="italic">
                  {concept.notes}
                </Text>
              )}
            </Box>
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default ConceptDetectionLog;